import {IOrganization} from "../../interfaces/IOrganization";
import {Grid, Mui, MuiIcons} from "@leap4/frontend";
import React, {useCallback, useContext, useMemo, useState} from "react";
import type {SelectChangeEvent} from "@mui/material/Select";

import {IOrganizationPartners} from "./PublisherLogins.container";
import {IOSSwitch} from "../../../App/IOSSwitch";
import {OrganizationContext} from "../OrganizationContex";
import {buildStatsUrl} from "../../../../utils/buildStatsUrl";
import {LoginUrlsTable} from "./LoginUrlsTable";

const {Autocomplete, Button, Chip, FormControlLabel, MenuItem, Select, TextField} = Mui;
const {Delete: DeleteIcon} = MuiIcons;


interface IPublisherLoginsAccessProps {
    organization: IOrganizationPartners,
    isLoading: boolean,
    handleSkipChange: (forOrganizationId: string, skip: boolean) => void,
    availableOrganizations: Array<IOrganization>,
    onUpdateSelectedOrgs: (selectedOrg: string, currentOrgId: string | undefined) => void,
    onPartnersChange: (choosenPartners: string[], currentOrgId: string) => void,
    onOrganizationRemove: (selectedOrg: string | undefined) => void,
    onFacadeNameChange: (facadeName: string | undefined, currentOrgId: string) => void,
}

interface IPartnerOption {
    id: string;
    name: string;
}

export const PublisherLoginsAccess = ({
                                          organization,
                                          isLoading,
                                          handleSkipChange,
                                          availableOrganizations,
                                          onUpdateSelectedOrgs,
                                          onPartnersChange,
                                          onOrganizationRemove,
                                          onFacadeNameChange
}: IPublisherLoginsAccessProps) => {

    const {org, selectedPartners, optionalPartners, toSkipDiscrepancy, facadeName} = organization
    const {clientCredentials, savedPartnersByOrg} = useContext(OrganizationContext);

    const handleOrgChange = ({target}: SelectChangeEvent) => {
        const currentOrgId = org?._id
        onUpdateSelectedOrgs(target.value, currentOrgId)
    }
    const [skip, setSkip] = useState<boolean>(toSkipDiscrepancy);

    const handlePartnersChange = (chosenPartners: string[]) => {
        onPartnersChange(chosenPartners, org._id)
    }

    const handleOrganizationRemove = () => {
        onOrganizationRemove(org._id)
    }

    const handleSkip = (event, checked: boolean) => {
        //organization.toSkipDiscrepancy = checked
        handleSkipChange(org._id, checked)
        setSkip(checked)
    }

    const handleChange = (e) => {
        let {value} = e.target;
        onFacadeNameChange(value, org?._id)
    }

    const validPartners: IPartnerOption[] = useMemo(() => {
        const arr = optionalPartners as IPartnerOption[];
        return arr[0]?.id ? arr : [];
    }, [optionalPartners]);

    const selectedAsOptions: IPartnerOption[] = useMemo(
        () => selectedPartners.map(id => {
            const found = validPartners.find(p => p.id === id);
            return found ?? {id, name: id};
        }),
        [selectedPartners, validPartners]
    );

    const unsavedPartnerIds: string[] = useMemo(() => {
        const saved = (org?._id && savedPartnersByOrg[org._id]) || [];
        return selectedPartners.filter(id => !saved.includes(id));
    }, [selectedPartners, savedPartnersByOrg, org?._id]);

    const canCopy = !!clientCredentials?.clientKey && !!org?._id;

    const buildUrl = useCallback(
        (supplyPartnerId: string) =>
            buildStatsUrl({
                supplyPartnerId,
                clientKey: clientCredentials?.clientKey ?? "",
                clientSecret: clientCredentials?.visibleSecret ?? "{ClientSecret}",
            }),
        [clientCredentials?.clientKey, clientCredentials?.visibleSecret]
    );

    if (optionalPartners[0]) {
        if (typeof optionalPartners[0] === "string") {
            return <></>;
        }
    }
    return (
        <Grid container  justifyContent={"space-between"} spacing={0.5} mb={2}>
            <Grid item xs={2}>

                <Select size={"small"} disabled={isLoading}
                        onChange={handleOrgChange}
                        value={org?._id || 'NULL'}
                        fullWidth
                >

                    <MenuItem disabled value={'NULL'}>
                        <em>Select Organization</em>
                    </MenuItem>

                    {availableOrganizations.map(o => {
                        return <MenuItem key={o._id} divider value={o?._id || 'ID'}>{o?.name}</MenuItem>
                    })}

                </Select>
            </Grid>
            <Grid item xs={2}>
                <TextField
                    size={'small'}
                    fullWidth
                    variant="outlined"
                    label="Facade name"
                    type="string"
                    disabled={!org?._id}
                    value={facadeName || ''}
                    onChange={handleChange}
                />
            </Grid>
            <Grid item xs={4.5}>
                <Autocomplete
                    multiple
                    size="small"
                    disabled={!org?._id || isLoading}
                    options={validPartners}
                    value={selectedAsOptions}
                    getOptionLabel={(option: IPartnerOption) => option?.name ?? ""}
                    isOptionEqualToValue={(option: IPartnerOption, value: IPartnerOption) => option?.id === value?.id}
                    filterSelectedOptions
                    onChange={(_event: React.SyntheticEvent, value: IPartnerOption[]) =>
                        handlePartnersChange(value.map(v => v.id))
                    }
                    renderTags={(values: IPartnerOption[], getTagProps: any) =>
                        values.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    label={option.name}
                                    size="small"
                                    color="primary"
                                />
                            );
                        })
                    }
                    renderInput={(params: any) => (
                        <TextField {...params} label={validPartners.length ? "Supply Partners" : "No Partners"} />
                    )}
                />
            </Grid>
            <Grid item xs={1.5}>
                <FormControlLabel
                    control={<IOSSwitch sx={{ m: 1 }}/>}
                    onChange={handleSkip}
                    checked={skip}
                    disabled={!org?._id}
                    label="To skip discrepancy"
                />
            </Grid>

            <Grid item xs={1.5}>
                <Button fullWidth variant="outlined" disabled={!org?._id} startIcon={<DeleteIcon/>}
                        onClick={handleOrganizationRemove}>
                    Delete
                </Button>

            </Grid>

            {selectedAsOptions.length > 0 && (
                <Grid item xs={12} mt={1}>
                    <LoginUrlsTable
                        partners={selectedAsOptions}
                        buildUrl={buildUrl}
                        disabled={!canCopy}
                        title="Supply Partners"
                        unsavedPartnerIds={unsavedPartnerIds}
                    />
                </Grid>
            )}

        </Grid>
    )
}
